import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Disc, Armchair, Wind, Footprints, Trash2, CircleOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { useUpdateProfile } from "@/hooks/use-auth";

const PREF_ITEMS = [
  { key: "steeringWheelCover", labelKey: "profile.steeringWheelCover", Icon: Disc },
  { key: "seatCovers", labelKey: "profile.seatCovers", Icon: Armchair },
  { key: "airFreshener", labelKey: "profile.airFreshener", Icon: Wind },
  { key: "floorCovers", labelKey: "profile.floorCovers", Icon: Footprints },
  { key: "trashBag", labelKey: "profile.trashBag", Icon: Trash2 },
  { key: "tissues", labelKey: "profile.tissues", Icon: CircleOff },
] as const;

interface SetPreferencesDialogProps {
  open: boolean;
  /** Called after preferences are saved successfully. */
  onSaved: () => void;
  /** Called when the user dismisses without saving. */
  onDismiss: () => void;
}

/**
 * Prompts an old customer (one without preferences set) to choose their
 * preferences before continuing to book. Mirrors the onboarding preferences UI
 * from Login.tsx. Bilingual via the existing `profile.*` locale keys.
 *
 * Dismissible — the customer can skip it and still book.
 */
export function SetPreferencesDialog({
  open,
  onSaved,
  onDismiss,
}: SetPreferencesDialogProps) {
  const { t } = useTranslation();
  const updateProfile = useUpdateProfile();
  const [prefs, setPrefs] = useState<Record<string, boolean>>({
    steeringWheelCover: true,
    seatCovers: true,
    airFreshener: true,
    floorCovers: true,
    trashBag: true,
    tissues: true,
  });

  const handleSave = async () => {
    try {
      await updateProfile.mutateAsync({ preferences: prefs });
      onSaved();
    } catch {
      // mutation error is surfaced by the hook's caller via toast if needed;
      // keep the dialog open so the user can retry.
    }
  };

  // Don't allow closing via overlay/escape to fire save while in-flight.
  const busy = updateProfile.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !busy) onDismiss();
      }}
    >
      <DialogContent className="max-w-md" data-testid="dialog-set-preferences">
        <DialogHeader>
          <DialogTitle>{t("booking.setPreferencesTitle")}</DialogTitle>
          <DialogDescription>{t("profile.preferencesDesc")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5 max-h-[45vh] overflow-y-auto">
          {PREF_ITEMS.map(({ key, labelKey, Icon }) => (
            <div
              key={key}
              className="flex items-center justify-between p-2 rounded-lg border"
            >
              <div className="flex items-center gap-2">
                <Icon className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm">{t(labelKey)}</span>
              </div>
              <Switch
                checked={prefs[key]}
                onCheckedChange={(v) =>
                  setPrefs((prev) => ({ ...prev, [key]: v }))
                }
              />
            </div>
          ))}
        </div>

        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            className="flex-1"
            onClick={onDismiss}
            disabled={busy}
          >
            {t("booking.skip")}
          </Button>
          <Button
            className="flex-1"
            onClick={handleSave}
            disabled={busy}
            data-testid="button-save-preferences-dialog"
          >
            {busy ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              t("login.saveAndContinue")
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
